import React, { use, useEffect, useState } from 'react'

import { useNavigate } from 'react-router-dom'
import { Box, BoxProps, Button, Text } from '@chakra-ui/react'

import { User } from '@pkg/types/user'

import api from '@/api'
import { HeaderContext } from '@/context/Header'
import { useUser } from '@/react-query/user'

import { ReferralBanner } from './ReferralBanner'

const planIdToName: Record<NonNullable<User['plan']>, string> = {
  FREE: 'Free',
  PRO: 'Pro',
  BUSINESS: 'Business',
}

const planToMaxAvailableInterviewTranscriptions: Record<
  NonNullable<User['plan']>,
  number
> = {
  FREE: 5,
  PRO: 40,
  BUSINESS: 1000000,
}

type PremiumHeaderProps = BoxProps

export const PremiumHeader: React.FC<PremiumHeaderProps> = (props) => {
  const navigate = useNavigate()

  const { referralModalDisclosure } = use(HeaderContext)

  const [isPortalLoading, setIsPortalLoading] = useState<boolean>(false)

  const user = useUser()

  const plan: User['plan'] = user.data?.plan || 'FREE'
  const billingPeriod: User['billingPeriod'] = user.data?.billingPeriod

  useEffect(() => {
    user.refetch()
  }, [])

  const handleOpenPricingPage: React.MouseEventHandler = async () => {
    navigate('/pricing-page')
  }

  const handleOpenPortal: React.MouseEventHandler = async (e) => {
    try {
      e.preventDefault()

      setIsPortalLoading(true)

      const { portalSessionUrl } = (await api.createPortalSession()).data

      window.location.href = portalSessionUrl
    } catch (error) {
      console.error(error)
      setIsPortalLoading(false)
    }
  }

  if (
    !user.data ||
    (user.data.availableInterviewTranscriptions || 0) >
      planToMaxAvailableInterviewTranscriptions.FREE ||
    plan === 'BUSINESS'
  ) {
    return (
      <>
        <ReferralBanner />
      </>
    )
  }

  return (
    <Box className="flex justify-center py-3 items-center px-8" {...props}>
      <Text fontWeight={'bold'} color={'rgba(0, 0, 0, 0.5)'}>
        {planIdToName[plan]} {plan === 'FREE' ? 'trial' : 'plan'}:{' '}
        {(user.data.availableInterviewTranscriptions || 0) <= 0 ? (
          'no more'
        ) : (
          <>
            <Text as="span" color="black">
              {user.data.availableInterviewTranscriptions} of{' '}
              {billingPeriod == 'MONTHLY' || plan === 'FREE'
                ? planToMaxAvailableInterviewTranscriptions[plan]
                : 12 * planToMaxAvailableInterviewTranscriptions[plan]}
            </Text>
          </>
        )}{' '}
        interview transcriptions available
        {plan === 'PRO' &&
          ` this ${billingPeriod === 'MONTHLY' ? 'month' : 'year'}`}
      </Text>

      {user.data.plan === 'FREE' && (
        <Box className={'flex sm:flex-row flex-col gap-[0.7rem] ml-[1rem] '}>
          <Button
            variant="brand-blue"
            className="shrink-0"
            onClick={handleOpenPricingPage}
          >
            Upgrade
          </Button>

          <Button
            variant="brand-solid-white"
            className="shrink-0"
            onClick={(e) => {
              e.stopPropagation()
              referralModalDisclosure.onOpen()
            }}
          >
            <Text> Refer a friend </Text>
            <Text className={'sm:inline hidden'}>
              &nbsp;and get a free month
            </Text>
          </Button>
        </Box>
      )}

      {user.data.plan === 'PRO' && (
        <Button
          variant="brand-blue"
          className="shrink-0"
          onClick={handleOpenPortal}
          marginLeft={'0.7rem'}
          isLoading={isPortalLoading}
        >
          Upgrade to Business
        </Button>
      )}
    </Box>
  )
}
